import { Component } from '@theme/component';

/**
 * WDW Case-Liste (Cases-Seite): Sticky-Navigation zu den untereinander stehenden Cases.
 * - Aktiver Case = der Case, der die Bildschirmmitte schneidet (IntersectionObserver); Zähler, Favicon
 *   (aria-current) und Pfeile folgen ihm.
 * - Pfeile springen zum vorherigen/nächsten sichtbaren Case, Favicons sind normale Anker-Links.
 * - „wdw:case-filter“ vom Seiten-Hero blendet Cases und Favicons nach Tag ein und aus.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} current - aktuelle Nummer
 * @property {HTMLElement} total - Anzahl sichtbarer Cases
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} [empty] - Hinweis, wenn der Filter nichts findet
 *
 * @extends {Component<Refs>}
 */
class WdwCaseListComponent extends Component {
  requiredRefs = ['current', 'total', 'prev', 'next'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {HTMLElement | undefined} */
  #active;

  connectedCallback() {
    super.connectedCallback();
    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '-45% 0px -50% 0px' });
    for (const item of this.cases) this.#observer.observe(item);
    document.addEventListener('wdw:case-filter', this.#onFilter);
    // ?filter=… selbst anwenden, falls das Ereignis des Seiten-Heros vor dieser Komponente kam
    const tag = new URL(window.location.href).searchParams.get('filter');
    if (tag) this.#onFilter(new CustomEvent('wdw:case-filter', { detail: { tag } }));
    else this.#setActive(this.visibleCases[0]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener('wdw:case-filter', this.#onFilter);
  }

  /** @returns {HTMLElement[]} */
  get cases() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-case]')]);
  }

  /** @returns {HTMLElement[]} */
  get visibleCases() {
    return this.cases.filter((item) => !item.hidden);
  }

  /** on:click="/previous" */
  previous() {
    this.#jump(-1);
  }

  /** on:click="/next" */
  next() {
    this.#jump(1);
  }

  /** @param {number} step */
  #jump(step) {
    const visible = this.visibleCases;
    const target = visible[(this.#active ? visible.indexOf(this.#active) : 0) + step];
    if (!target) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    this.#setActive(target);
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    const hit = entries.find((entry) => entry.isIntersecting && !(/** @type {HTMLElement} */ (entry.target).hidden));
    if (hit) this.#setActive(/** @type {HTMLElement} */ (hit.target));
  };

  /** @param {Event} event */
  #onFilter = (event) => {
    const tag = /** @type {CustomEvent<{ tag: string }>} */ (event).detail?.tag ?? '';
    const matches = (/** @type {HTMLElement} */ element) =>
      !tag || (element.dataset.tags ?? '').split(' ').includes(tag);

    for (const item of this.cases) {
      const show = matches(item);
      if (show && item.hidden) item.dataset.filterState = 'in';
      else delete item.dataset.filterState;
      item.hidden = !show;
    }
    for (const thumb of this.querySelectorAll('.wdw-case-list__thumbs li')) {
      if (thumb instanceof HTMLElement) thumb.hidden = !matches(thumb);
    }

    const visible = this.visibleCases;
    this.refs.total.textContent = String(visible.length);
    if (this.refs.empty) this.refs.empty.hidden = visible.length > 0;
    this.#setActive(visible[0]);
  };

  /** @param {HTMLElement | undefined} item */
  #setActive(item) {
    this.#active = item;
    const visible = this.visibleCases;
    const index = item ? visible.indexOf(item) : -1;

    for (const link of this.querySelectorAll('[data-case-link]')) {
      const current = item !== undefined && link.getAttribute('href') === `#${item.id}`;
      if (current) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }

    this.refs.current.textContent = String(Math.max(index + 1, visible.length ? 1 : 0));
    this.refs.prev.disabled = index <= 0;
    this.refs.next.disabled = index < 0 || index >= visible.length - 1;
  }
}

if (!customElements.get('wdw-case-list-component')) {
  customElements.define('wdw-case-list-component', WdwCaseListComponent);
}
